import { useCallback } from "react";

import { Box, Flex, Image, useDisclosure } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { PageName } from "analytics/events";
import {
  ConfirmLargeQuantityChangeModal,
  shouldShowLargeQuantityChangeModal,
} from "flows/Inbound/components/ConfirmLargeQuantityChangeModal";
import { TaskCheckHeader } from "flows/Inventory/shared/components/TaskCheckHeader";
import { Page } from "shared/components/Page";
import { transformChunks } from "shared/components/TextWrapper";
import { Button } from "ui/Button/Button";
import { IncreaseDecrease } from "ui/IncreaseDecrease/IncreaseDecrease";
import { BodyS, HeaderM, TitleS } from "ui/Typography/Typography";
import { formatImage } from "utils/image";
import { productNameWithCountry } from "utils/product";
import { isNotNullNorUndefined } from "utils/tsHelpers";

import { useIDCData } from "../hooks/useIDCData";
import { useIDCService } from "../hooks/useIDCService";
import { useProductStock } from "../hooks/useProductStock";

const IMAGE_SIZE = 218;

export function InboundDeviationCheckQuantityPage() {
  const intl = useIntl();
  const { IDCData, customAttributes, isLoading: isLoadingIDCData } = useIDCData();
  const { stock: productStock, loading: isLoadingStock } = useProductStock(IDCData?.sku);
  const { countedItems, setCountedItems, validateIDC } = useIDCService();
  const expectedQuantity = customAttributes?.expectedQuantity ?? 0;

  const imageUrlFormatted = IDCData?.productImageUrl
    ? formatImage(IDCData.productImageUrl, IMAGE_SIZE, IMAGE_SIZE)
    : null;

  const { isOpen: isModalOpen, onOpen: openModal, onClose: closeModal } = useDisclosure();

  const handleValidateIDC = useCallback(() => {
    if (!IDCData) {
      return;
    }
    validateIDC({
      checkId: IDCData.checkId,
      sku: IDCData.sku,
      quantity: countedItems,
      expectedQuantity,
      shelfNumber: IDCData.shelfNumber,
      shelfLetter: IDCData.shelfLetter,
    });
  }, [IDCData, countedItems, expectedQuantity, validateIDC]);

  const handleSubmit = useCallback(() => {
    if (shouldShowLargeQuantityChangeModal(expectedQuantity, countedItems)) {
      openModal();
    } else {
      handleValidateIDC();
    }
  }, [expectedQuantity, countedItems, openModal, handleValidateIDC]);

  const handleConfirm = useCallback(() => {
    closeModal();
    handleValidateIDC();
  }, [closeModal, handleValidateIDC]);
  const isLoading = isLoadingIDCData || isLoadingStock;
  return (
    <Page isCentered isBgGrey isFull isLoading={isLoading}>
      <TaskCheckHeader
        headerMessageId="pages.inventory.inbound-deviation-checks.title"
        origin={PageName.INVENTORY_INBOUND_DEVIATION_CHECK_LIST_PAGE}
        shelfLetter={IDCData?.shelfLetter ?? ""}
        shelfNumber={IDCData?.shelfNumber ?? ""}
      />
      <Flex flex={1} flexDir="column" overflowY="scroll" w="100%">
        <ConfirmLargeQuantityChangeModal
          onClickConfirm={handleConfirm}
          onClickDecline={closeModal}
          isOpen={isModalOpen}
          quantity={countedItems}
        />
        <Flex flex={1} align="center" flexDir="column" justify="center" px="s100">
          <Flex flex={1} align="center">
            {isNotNullNorUndefined(imageUrlFormatted) && (
              <Image
                src={imageUrlFormatted}
                objectFit="contain"
                h="60vh"
                maxW={`${IMAGE_SIZE}px`}
                maxH={`${IMAGE_SIZE}px`}
              />
            )}
          </Flex>
          <Flex px="s200" gap="s100" mb="s100">
            <Flex
              justifyContent="center"
              alignItems="center"
              px="s200"
              py="s100"
              bg="white"
              borderRadius="sm"
              alignSelf="stretch"
              w="100%"
            >
              <BodyS textAlign="center" color="grey.800" data-testid="stock-check-product-name">
                {productNameWithCountry(
                  IDCData?.productName ?? "",
                  IDCData?.productCountryOfOriginName ?? "",
                )}
              </BodyS>
            </Flex>
            <Flex
              direction="column"
              justifyContent="center"
              alignItems="center"
              p="s100"
              bg="white"
              borderRadius="sm"
            >
              <TitleS color="grey.800">
                {intl.formatMessage({
                  id: "flows.inventory.checks.bbd-checks.pages.bbd-check-declare-expired.stock-label",
                })}
              </TitleS>
              <HeaderM data-testid="product-stock">{productStock}</HeaderM>
            </Flex>
          </Flex>
        </Flex>
        <Box bg="white" p="s200" gap="s50">
          <Box mx="s100">
            <IncreaseDecrease
              label={
                <FormattedMessage
                  id="components.increasedecrease.how_many_units"
                  values={transformChunks({ b: { as: "strong" } })}
                />
              }
              changeHandler={setCountedItems}
              count={countedItems}
              withInput
            />
          </Box>
          <Button
            size="lg"
            mt="s300"
            isFullWidth
            onClick={handleSubmit}
            data-testid="submit-quantity-button"
          >
            {intl.formatMessage({ id: "pages.task.taskIndex.confirm_amount" })}
          </Button>
        </Box>
      </Flex>
    </Page>
  );
}
